<template>
  <article>
    <h1 class="text-h3" id="intro" v-intersect="onIntersect">Introduction</h1>
    <p>
      This user guide contains functional documentation related to features available in
      <router-link :to="{ path: '/help/about' }">AlphaX Sky</router-link>.
    </p>

    <h2 class="text-h4" id="interface" v-intersect="onIntersect">User Interface</h2>
    <v-card flat outlined color="grey">
      <v-img eager contain src="@/assets/images/help_interface.png"></v-img>
    </v-card>
    <p>The user interface consists of some primary components, namely:</p>
    <ul>
      <li><a href="#toolbar">application toolbar</a></li>
      <li><a href="#drawer">navigation drawer</a></li>
      <li><a href="#toolbars">toolbars</a></li>
      <li><a href="#statusbar">status bar</a></li>
    </ul>

    <h3 class="text-h5" id="appbar" v-intersect="onIntersect">Application toolbar</h3>
    <p>The blue area on the top displays information and actions relating to the entire application.</p>
    <v-card flat outlined color="grey">
      <v-img eager contain src="@/assets/images/help_appbar.png"></v-img>
    </v-card>
    <ul>
      <li>Click the application title to navigate back to home page.</li>
      <li><v-icon>mdi-menu</v-icon> switches between mini and expanded modes of the navigation drawer.</li>
      <li><v-icon>mdi-theme-light-dark</v-icon> toggles between light and dark color modes.</li>
      <li><v-icon>mdi-refresh</v-icon> refreshes data for the current screen.</li>
      <li><v-icon>mdi-help-circle-outline</v-icon> opens these in-app help pages.</li>
      <li><v-icon>mdi-cog</v-icon> links to the management console (admninistrators only)</li>
      <li><v-icon>mdi-exit-to-app</v-icon> logs the current user out of the application.</li>
    </ul>

    <h3 class="text-h5" id="drawer" v-intersect="onIntersect">Navigation Drawer</h3>
    <p>
      The dark grey bar to the left provides access to available functionality in the system.
    </p>
    <p>Clicking <v-icon>mdi-menu</v-icon> in the appbar toggles the state of the drawer between mini and expanded verions.
    </p>
    <v-card flat outlined color="grey">
      <div class="d-flex flex-no-wrap justify-space-between">
        <v-img eager contain max-height="300" src="@/assets/images/help_drawer_mini.png"></v-img>
        <v-img eager contain max-height="300" src="@/assets/images/help_drawer_large.png"></v-img>
      </div>
    </v-card>

    <h3 class="text-h5" id="toolbar" v-intersect="onIntersect">Toolbars</h3>
    <p>
      Most components in the main workspace use a toolbar that provides access to relevant functionality. The toolbar is sometimes supplmented by tabs that
      help navigate between different sections of the same page. Toolbars generally consist of the following elements.
    </p>
    <v-card flat outlined color="grey">
      <v-img eager contain src="@/assets/images/help_toolbar.png"></v-img>
    </v-card>
    <ul>
      <li>a title that provides information on current route, and can be used to rename individual objects</li>
      <li>any number of buttons that navigate to relevant pages or perform specific functions</li>
      <li>a text field that allows searching within listed items (list views)</li>
      <li>a count of loaded and total items in the list (list views)</li>
    </ul>
    <p>
      Toolbar buttons change depending on the context. Buttons disappear when a function is not available. 
      Disabled icons indicate a functionality not available to current user.
    </p>
    <v-alert type="info" text border="left">
      All toolbar buttons employ toolips
      <v-tooltip top transition="none" color="black">
        <template v-slot:activator="{ on, attrs }"><v-icon light v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon></template>
        <span>This is a tooltip</span>
      </v-tooltip>
      that convery information when a user hovers over them.
    </v-alert>

    <h3 class="text-h5" id="statusbar" v-intersect="onIntersect">Status bar</h3>
    <p>The small component at the bottom displays system status information to the user.</p>
    <v-card flat outlined color="grey">
      <v-img eager contain src="@/assets/images/help_statusbar.png"></v-img>
    </v-card>
    <p>
      The status bar provides information on current application version, and logged in user. It also informs if the backend server is available
      <v-icon color="green">mdi-check-circle</v-icon>
      or
      <v-icon color="red">mdi-alert-circle</v-icon>
      not. The server is required for some backend processing, and must be online when requesting long-running tasks.
    </p>
    <v-alert type="info" text border="left">
      If the server appears offline, refreshing the page should turn the icon green. 
    </v-alert>

    <h2 class="text-h4" id="layouts" v-intersect="onIntersect">Layouts</h2>
    <p>
      The user interface consists of some typical layouts that help organize content. These layouts are based on common usage patterns found elsewhere on
      the internet.
    </p>

    <h3 class="text-h5" id="listview" v-intersect="onIntersect">List view</h3>
    <p>Multiple items are displayed in a scrolling list. The list loads as many items as can fit on a screen, and more when the user scrolls.</p>

    <v-card flat outlined color="grey">
      <v-img eager contain src="@/assets/images/file_table.png"></v-img>
    </v-card>

    <p>List items are clickable and navigate to detail view of clicked item.</p>
    <p>Users can search through the list and sort by name or date. Recent items appear on top by default.</p>
    <p>The <em>Status</em> column indicates whether item is available for viewing or not. <v-icon>mdi-alert-outline</v-icon> indicates a problem with an item.</p>
    <p>Multiple items can be selected by clicking the checkbox. </p>

    <h3 class="text-h5" id="detailview" v-intersect="onIntersect">Detail view</h3>
    <p>The detail view of an item provides indepth look at the contents. Information is split into tabs, each with different content. Contextual actions are available in the toolbar.</p>
    <v-card flat outlined color="grey">
      <v-img eager contain src="@/assets/images/file_detail.png"></v-img>
    </v-card>
    <p>More information on detail view is available in respective help sections.</p>

    <h2 class="text-h4" id="functions" v-intersect="onIntersect">Core functions</h2>
    <p>
      This system is designed as a suite of applications that perform certain functions. Each application has its own requirements for data and
      processing needs. To cater to these specific needs, data is organized into different objects, each catering to a specific need or application requirement.</p>

    <h3 class="text-h5" id="files" v-intersect="onIntersect">
      <v-icon large>mdi-file-document-outline</v-icon>
      Files
    </h3>
    <div class="d-flex flex-no-wrap justify-space-between mb-4">
      <p>
        <router-link :to="{ path: '/help/files' }">Files</router-link>
        are the primary source of data for all applications. User data is
        <router-link :to="{ path: '/help/files', hash: '#upload' }">uploaded</router-link>
        and maintained in files. Users can upload data from their own systems or
        <router-link :to="{ path: '/help/files', hash: '#import' }">import</router-link>
        from enterprise databases, provided requisite access is granted. Files can be consumed directly within
        <router-link :to="{ path: '/help/applications' }">applications</router-link>
        or need to be grouped or processed via datasets.
      </p>
    </div>

    <h3 class="text-h5" id="datasets" v-intersect="onIntersect">
      <v-icon large>mdi-database</v-icon>
      Datasets
    </h3>
    <div class="d-flex flex-no-wrap justify-space-between mb-4">
      <p>
        <router-link :to="{ path: '/help/datasets' }">Datasets</router-link>
        are a collection of files and
        <router-link :to="{ path: '/help/datasets', hash: '#filters' }">filters</router-link>
        that allow users to create compilations of data. Filters can be applied to create multiple subsets. A dataset is an interim structure to help organize
        data in files, and for predictions to consume the data more easily. Datasets can be used on their own within applications.
      </p>
    </div>

    <h3 class="text-h5" id="predictions" v-intersect="onIntersect">
      <v-icon large>mdi-finance</v-icon>
      Predictions
    </h3>
    <div class="d-flex flex-no-wrap justify-space-between mb-4">
      <p>
        <router-link :to="{ path: '/help/predictions' }">Predictions</router-link>
        are outputs of offline machine learning processes that allow users to
        <router-link :to="{ path: '/help/predictions', hash: '#generate' }">generate</router-link>
        a large number of predictions for later consumption. Some
        <router-link :to="{ path: '/help/applications' }">applications</router-link>
        require the use of predictions to offload most of the computationally intensive workload to the server. Predictions use datasets as their primary
        inputs, and can be
        <router-link :to="{ path: '/help/predictions', hash: '#export' }">exported</router-link>
        to other formats.
      </p>
    </div>

    <h3 class="text-h5" id="applications" v-intersect="onIntersect">
      <v-icon large>mdi-apps</v-icon>
      Applications
    </h3>
    <div class="d-flex flex-no-wrap justify-space-between mb-4">
      <p>
        <router-link :to="{ path: '/help/applications' }">Applications</router-link>
        are the primary work unit and offer specific functionality not available elsewhere. Applications use files, datasets, or
        predictions to work their magic. Applications are available in two flavors; online and offline.
      </p>
    </div>

    <v-alert outlined text type="info">
      <strong>Note:</strong>
      Some applications consume files directly, while others require datasets processed as predictions. Each application has its own requirements.
    </v-alert>

    <contents :items="menu" ref="contents" />
    
  </article>
</template>
<script>
import Contents from "@/components/contents"
export default {
  name: "HelpHome",
  components: { Contents },
  data() {
    return {
      selected: "intro",
      menu: [
        { id: "intro", text: "Introduction", depth: 1 },
        { id: "interface", text: "User interface", depth: 1 },
        { id: "appbar", text: "Application bar", depth: 2 },
        { id: "drawer", text: "Navigation drawer", depth: 2 },
        { id: "toolbar", text: "Toolbars", depth: 2 },
        { id: "statusbar", text: "Status bar", depth: 2 },
        { id: "layouts", text: "Layouts", depth: 1 },
        { id: "listview", text: "Listv view", depth: 2 },
        { id: "detailview", text: "Detail view", depth: 2 },
        { id: "functions", text: "Core functions", depth: 1 },
        { id: "files", text: "Files", depth: 2 },
        { id: "datasets", text: "Datasets", depth: 2 },
        { id: "predictions", text: "Predictions", depth: 2 },
        { id: "applications", text: "Applications", depth: 2 },
      ],
    }
  },
}
</script>
